import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Transaction, CreateTransaction, UpdateTransaction, TransactionFilter, Customer, Project, Dashboard as DashboardType } from '../types';
import { transactionsAPI, customersAPI, projectsAPI, dashboardAPI } from '../services/api';
import TransactionsTable from './TransactionsTable';
import Pagination from './Pagination';
import TransactionForm from './TransactionForm';
import SearchableDropdown from './SearchableDropdown';
import DateInput from './DateInput';
import CustomerDebtTable from './CustomerDebtTable';
import { useTransactionDirections } from '../hooks/useTransactionDirections';
import { useTransactionTypes } from '../hooks/useTransactionTypes';
import { useTransactionCategories } from '../hooks/useTransactionCategories';
import { saveFiltersToStorage, loadFiltersFromStorage } from '../utils/filterStorage';
import { TransactionCategory, TransactionDirection, TRANSACTION_CATEGORY_LABELS, TRANSACTION_DIRECTION_LABELS } from '../utils/constants';
import { usePagination } from '../hooks/usePagination';

type TransactionsView = 'all' | 'incompleted' | 'topDebts' | 'tbu' | 'latestUpdated';

const TRANSACTIONS_FILTER_STORAGE_PAGE = 'transactions';

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const createDefaultTransactionFilters = (): TransactionFilter => ({
  category: TransactionCategory.AllCategories,
  customerId: 0,
  directionId: TransactionDirection.All,
  sourceId: TransactionDirection.NoValue,
  currencyId: 0,
  transactionTypeId: 0,
  projectId: 0,
  startDate: toIsoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
  endDate: toIsoDate(new Date()),
  searchText: '',
  skip: 0,
  take: 50,
});

const coerceNumberFilter = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const coerceStringFilter = (value: unknown, fallback: string): string => (
  typeof value === 'string' ? value : fallback
);

const sanitizeTransactionFilters = (
  value: Partial<TransactionFilter>,
  defaults: TransactionFilter
): TransactionFilter => ({
  category: coerceNumberFilter(value.category, defaults.category ?? TransactionCategory.AllCategories),
  customerId: coerceNumberFilter(value.customerId, defaults.customerId ?? 0),
  directionId: coerceNumberFilter(value.directionId, defaults.directionId ?? TransactionDirection.All),
  sourceId: coerceNumberFilter(value.sourceId, defaults.sourceId ?? TransactionDirection.NoValue),
  currencyId: coerceNumberFilter(value.currencyId, defaults.currencyId ?? 0),
  transactionTypeId: coerceNumberFilter(value.transactionTypeId, defaults.transactionTypeId ?? 0),
  projectId: coerceNumberFilter(value.projectId, defaults.projectId ?? 0),
  startDate: coerceStringFilter(value.startDate, defaults.startDate ?? ''),
  endDate: coerceStringFilter(value.endDate, defaults.endDate ?? ''),
  searchText: coerceStringFilter(value.searchText, defaults.searchText ?? ''),
  skip: 0,
  take: coerceNumberFilter(value.take, defaults.take ?? 50),
});

const omitPaginationFilters = (filter: TransactionFilter): Partial<TransactionFilter> => {
  const { skip, take, ...filtersToSave } = filter;
  return filtersToSave;
};

const saveTransactionFilters = (filter: TransactionFilter): void => {
  saveFiltersToStorage(TRANSACTIONS_FILTER_STORAGE_PAGE, omitPaginationFilters(filter));
};

const hasStoredTransactionFilters = (): boolean => {
  try {
    return localStorage.getItem(`filter-${TRANSACTIONS_FILTER_STORAGE_PAGE}`) !== null;
  } catch (error) {
    console.error('Failed to check saved transaction filters:', error);
    return false;
  }
};

const Transactions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editIdParam = searchParams.get('editId');
  const isDeepLinkedEdit = Boolean(editIdParam);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [referenceDataLoading, setReferenceDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | undefined>(undefined);
  const [copyingTransaction, setCopyingTransaction] = useState<Transaction | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);
  const [transactionFormDataLoading, setTransactionFormDataLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [activeView, setActiveView] = useState<TransactionsView>('all');
  const [serverSearchActive, setServerSearchActive] = useState(false);
  const [dashboardSnapshot, setDashboardSnapshot] = useState<DashboardType | null>(null);
  const [tbuTransactions, setTbuTransactions] = useState<Transaction[]>([]);
  const confirm = useConfirm();
  const { directions, loading: directionsLoading } = useTransactionDirections();
  const { types: transactionTypes, loading: transactionTypesLoading } = useTransactionTypes();
  const { categories: transactionCategories, loading: transactionCategoriesLoading } = useTransactionCategories();

  // Pagination
  const [, setCurrentPage] = useState(0);

  const defaultFilters = useMemo(() => createDefaultTransactionFilters(), []);
  const [hasRestoredFilters] = useState(() => hasStoredTransactionFilters());

  // Filters
  const [filter, setFilter] = useState<TransactionFilter>(() => sanitizeTransactionFilters(
    loadFiltersFromStorage(TRANSACTIONS_FILTER_STORAGE_PAGE, defaultFilters),
    defaultFilters
  ));
  const initialFilterRef = useRef(filter);

  const projectCustomerMap = useMemo(() => {
    const map = new Map<number, number>();
    projects.forEach(project => {
      map.set(project.id, project.customerId);
    });
    return map;
  }, [projects]);

  const filteredTransactions = useMemo(() => {
    const searchValue = filter.searchText?.trim().toLowerCase() ?? '';
    const startDate = filter.startDate ? new Date(`${filter.startDate}T00:00:00`) : null;
    const endDate = filter.endDate ? new Date(`${filter.endDate}T23:59:59.999`) : null;

    return (transactions ?? []).filter(transaction => {
      const matchesType =
        filter.transactionTypeId === 0 || transaction.typeId === filter.transactionTypeId;

      const matchesSource =
        filter.sourceId === TransactionDirection.All ||
        (filter.sourceId === TransactionDirection.NoValue
          ? transaction.sourceId === undefined || transaction.sourceId === null
          : transaction.sourceId === filter.sourceId);

      const matchesDirection =
        filter.directionId === TransactionDirection.All ||
        (filter.directionId === TransactionDirection.NoValue
          ? transaction.directionId === undefined || transaction.directionId === null
          : transaction.directionId === filter.directionId);

      const matchesCategory =
        filter.category === TransactionCategory.AllCategories ||
        (filter.category === TransactionCategory.NoCategory
          ? transaction.category === undefined || transaction.category === null
          : transaction.category === filter.category);

      const matchesProject =
        filter.projectId === 0 || transaction.projectId === filter.projectId;

      const transactionCustomerId = projectCustomerMap.get(transaction.projectId);
      const matchesCustomer =
        filter.customerId === 0 || transactionCustomerId === filter.customerId;

      const matchesSearch =
        !searchValue ||
        (transaction.title ?? '').toLowerCase().includes(searchValue) ||
        transaction.description?.toLowerCase().includes(searchValue) ||
        transaction.projectTitle?.toLowerCase().includes(searchValue) ||
        transaction.customerTitle?.toLowerCase().includes(searchValue);

      const transactionDate = new Date(transaction.date);
      const matchesStartDate = !startDate || transactionDate >= startDate;
      const matchesEndDate = !endDate || transactionDate <= endDate;

      return (
        matchesType &&
        matchesSource &&
        matchesDirection &&
        matchesCategory &&
        matchesProject &&
        matchesCustomer &&
        matchesSearch &&
        matchesStartDate &&
        matchesEndDate
      );
    });
  }, [transactions, filter, projectCustomerMap]);

  const {
    currentPage,
    pageSize,
    paginatedData: paginatedTransactions,
    totalItems,
    goToPage,
    setPageSize
  } = usePagination({ data: filteredTransactions, initialPageSize: 10 });

  const incompletedTransactions = dashboardSnapshot?.transactionsNotCompleted ?? [];
  const topCustomerDebts = dashboardSnapshot?.topCustomersWithDebt ?? [];
  const latestUpdatedTransactions = useMemo(() => {
    const getUpdatedTimestamp = (transaction: Transaction) => {
      const lastUpdated = transaction.updatedDate ?? transaction.createdDate ?? transaction.date;
      return new Date(lastUpdated).getTime();
    };

    return [...transactions].sort((a, b) => getUpdatedTimestamp(b) - getUpdatedTimestamp(a));
  }, [transactions]);

  const {
    currentPage: incompletedCurrentPage,
    pageSize: incompletedPageSize,
    paginatedData: paginatedIncompletedTransactions,
    totalItems: incompletedTotalItems,
    goToPage: goToIncompletedPage,
    setPageSize: setIncompletedPageSize
  } = usePagination({ data: incompletedTransactions, initialPageSize: 10 });

  const {
    currentPage: debtsCurrentPage,
    pageSize: debtsPageSize,
    paginatedData: paginatedCustomerDebts,
    totalItems: debtsTotalItems,
    goToPage: goToDebtsPage,
    setPageSize: setDebtsPageSize
  } = usePagination({ data: topCustomerDebts, initialPageSize: 10 });

  const {
    currentPage: tbuCurrentPage,
    pageSize: tbuPageSize,
    paginatedData: paginatedTbuTransactions,
    totalItems: tbuTotalItems,
    goToPage: goToTbuPage,
    setPageSize: setTbuPageSize
  } = usePagination({ data: tbuTransactions, initialPageSize: 10 });

  const {
    currentPage: latestUpdatedCurrentPage,
    pageSize: latestUpdatedPageSize,
    paginatedData: paginatedLatestUpdatedTransactions,
    totalItems: latestUpdatedTotalItems,
    goToPage: goToLatestUpdatedPage,
    setPageSize: setLatestUpdatedPageSize
  } = usePagination({ data: latestUpdatedTransactions, initialPageSize: 10 });

  function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(d.setDate(diff));
  }

  function getStartOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  function getEndOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  useEffect(() => {
    loadData({ includeProjectFilters: !isDeepLinkedEdit });
  }, [isDeepLinkedEdit]);

  useEffect(() => {
    // Handle URL parameters for preselecting project
    const projectId = searchParams.get('projectId');
    if (projectId) {
      setFilter(prev => ({ ...prev, projectId: parseInt(projectId) }));
    }

    const view = searchParams.get('view');
    switch (view) {
      case 'all':
        setActiveView('all');
        break;
      case 'incompleted':
        setActiveView('incompleted');
        break;
      case 'top-customer-debts':
      case 'topdebts':
        setActiveView('topDebts');
        break;
      case 'tbu':
        setActiveView('tbu');
        break;
      case 'latest-updated':
      case 'latestupdated':
        setActiveView('latestUpdated');
        break;
      default:
        break;
    }
    
    // Handle editId parameter for editing a transaction
    if (editIdParam) {
      loadTransactionForEdit(parseInt(editIdParam));
    }
  }, [searchParams, editIdParam]);


  const loadData = async ({ includeProjectFilters = true }: { includeProjectFilters?: boolean } = {}) => {
    try {
      setReferenceDataLoading(true);
      setLoading(true);
      const [customersData, projectsData] = await Promise.all([
        customersAPI.getCustomers(),
        includeProjectFilters
          ? projectsAPI.getProjects({ take: 10000 })
          : Promise.resolve({ items: [], totalCount: 0 })
      ]);

      setCustomers(customersData);
      setProjects(projectsData.items);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setReferenceDataLoading(false);
      setLoading(false);
    }
  };

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const [result, dashboardData, tbuData] = await Promise.all([
        transactionsAPI.getTransactions(0, 10000),
        dashboardAPI.getDashboard(new Date().getFullYear()),
        transactionsAPI.getTbuTransactions().catch(() => [])
      ]);
      setTransactions(result);
      setTotalCount(result.length);
      setDashboardSnapshot(dashboardData);

      const normalizedTbu = tbuData
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setTbuTransactions(normalizedTbu);
      setServerSearchActive(false);
      
      setError(null);
    } catch (err) {
      setError('Failed to load transactions');
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const runFilteredSearch = useCallback(async (nextFilter: TransactionFilter) => {
    try {
      setLoading(true);
      const searchFilter = sanitizeTransactionFilters(
        {
          ...nextFilter,
          skip: 0,
          take: nextFilter.take ?? defaultFilters.take,
        },
        defaultFilters
      );
      const result = await transactionsAPI.getTransactionsFiltered(searchFilter);
      const transactions = Array.isArray(result)
        ? result
        : (result?.transactions ?? []);
      setTransactions(transactions);
      setTotalCount(
        Array.isArray(result) ? transactions.length : (result?.count ?? transactions.length)
      );
      setServerSearchActive(true);
      setError(null);
    } catch (err) {
      setError('Failed to search transactions');
      console.error('Error searching transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [defaultFilters]);

  const updateSavedTransactionInState = useCallback((savedTransaction: Transaction, mode: 'create' | 'update') => {
    setTransactions(prev => {
      const withoutSaved = prev.filter(transaction => transaction.id !== savedTransaction.id);
      const nextTransactions = mode === 'create'
        ? [savedTransaction, ...withoutSaved]
        : prev.map(transaction => transaction.id === savedTransaction.id ? savedTransaction : transaction);

      return [...nextTransactions]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });

    setTotalCount(prev => mode === 'create' ? prev + 1 : prev);

    setDashboardSnapshot(prev => {
      if (!prev) return prev;

      const withoutSaved = prev.transactionsNotCompleted.filter(transaction => transaction.id !== savedTransaction.id);
      const shouldShowAsIncomplete = !savedTransaction.isCompleted;

      return {
        ...prev,
        transactionsNotCompleted: shouldShowAsIncomplete
          ? [savedTransaction, ...withoutSaved].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          : withoutSaved,
      };
    });

    setTbuTransactions(prev => {
      const withoutSaved = prev.filter(transaction => transaction.id !== savedTransaction.id);
      const normalizedTitle = (savedTransaction.title ?? '').toLowerCase();
      const isTbu = normalizedTitle.includes('tbu') || normalizedTitle.includes('to be updated');

      if (!isTbu) {
        return withoutSaved;
      }

      return [savedTransaction, ...withoutSaved]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });
  }, []);

  // Load initial data when component mounts
  useEffect(() => {
    if (!isDeepLinkedEdit) {
      if (hasRestoredFilters) {
        runFilteredSearch(initialFilterRef.current);
      } else {
        loadTransactions();
      }
    }
  }, [hasRestoredFilters, isDeepLinkedEdit, loadTransactions, runFilteredSearch]);

  const loadTransactionForEdit = async (id: number) => {
    try {
      setShowForm(true);
      setEditingTransaction(undefined);
      setCopyingTransaction(undefined);
      setTransactionFormDataLoading(true);
      const transaction = await transactionsAPI.getTransaction(id);
      setEditingTransaction(transaction);
    } catch (err) {
      setError('Failed to load transaction for editing');
      setShowForm(false);
      console.error('Error loading transaction:', err);
    } finally {
      setTransactionFormDataLoading(false);
    }
  };

  const handleCreateTransaction = async (transactionData: CreateTransaction) => {
    try {
      setFormLoading(true);
      const savedTransaction = await transactionsAPI.createTransaction(transactionData);
      updateSavedTransactionInState(savedTransaction, 'create');
      setShowForm(false);
      setEditingTransaction(undefined);
      setCopyingTransaction(undefined);
    } catch (err) {
      setError('Failed to create transaction');
      console.error('Error creating transaction:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTransaction = async (transactionData: UpdateTransaction) => {
    try {
      setFormLoading(true);
      const savedTransaction = await transactionsAPI.updateTransaction(transactionData.id, transactionData);
      updateSavedTransactionInState(savedTransaction, 'update');
      setShowForm(false);
      setEditingTransaction(undefined);
      setCopyingTransaction(undefined);

      // If we came from another page with editId parameter, go back
      if (searchParams.get('editId')) {
        navigate(-1);
      }
    } catch (err) {
      setError('Failed to update transaction');
      console.error('Error updating transaction:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTransaction = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete transaction',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await transactionsAPI.deleteTransaction(id);
      await loadTransactions();
    } catch (err) {
      setError('Failed to delete transaction');
      console.error('Error deleting transaction:', err);
    }
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setCopyingTransaction(undefined);
    setShowForm(true);
  };

  const handleCopyTransaction = (transaction: Transaction) => {
    // Add redirect parameter to return to transactions page after copying
    const url = `/transactions/create?copyId=${transaction.id}`;
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('redirect', '/transactions');
    navigate(urlObj.pathname + urlObj.search);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTransaction(undefined);
    setCopyingTransaction(undefined);

    // If we came from another page with editId parameter, go back
    if (searchParams.get('editId')) {
      navigate(-1);
    }
  };

  const handleFormDelete = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete transaction',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await transactionsAPI.deleteTransaction(id);
      setShowForm(false);
      setEditingTransaction(undefined);
      setCopyingTransaction(undefined);

      // If we came from another page with editId parameter, go back
      if (searchParams.get('editId')) {
        navigate(-1);
      }
    } catch (err) {
      setError('Failed to delete transaction');
      console.error('Error deleting transaction:', err);
    }
  };

  const handleFormSubmit = (transactionData: CreateTransaction | UpdateTransaction) => {
    if ('id' in transactionData) {
      handleUpdateTransaction(transactionData);
    } else {
      handleCreateTransaction(transactionData);
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
    const updatedFilter = sanitizeTransactionFilters({ ...filter, skip: 0 }, defaultFilters);
    setFilter(updatedFilter);
    saveTransactionFilters(updatedFilter);
    runFilteredSearch(updatedFilter);
  };

  const handleFilterChange = (field: keyof TransactionFilter, value: any) => {
    const updatedFilter = sanitizeTransactionFilters({ ...filter, [field]: value, skip: 0 }, defaultFilters);

    saveTransactionFilters(updatedFilter);
    setCurrentPage(0);
    setFilter(updatedFilter);

    if (field !== 'searchText' && serverSearchActive) {
      runFilteredSearch(updatedFilter);
    }
  };

  const handleClearFilters = () => {
    const resetFilter = sanitizeTransactionFilters(defaultFilters, defaultFilters);
    setCurrentPage(0);
    setFilter(resetFilter);
    saveTransactionFilters(resetFilter);
    loadTransactions();
  };

  const applyDatePreset = (
    preset: 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear'
  ) => {
    const today = new Date();
    let startDate = today;
    let endDate = today;

    switch (preset) {
      case 'today':
        startDate = today;
        endDate = today;
        break;
      case 'thisWeek':
        startDate = getStartOfWeek(today);
        endDate = today;
        break;
      case 'thisMonth':
        startDate = getStartOfMonth(today);
        endDate = today;
        break;
      case 'lastMonth': {
        const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        startDate = getStartOfMonth(lastMonthDate);
        endDate = getEndOfMonth(lastMonthDate);
        break;
      }
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      case 'lastYear':
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        break;
    }

    const nextFilter: TransactionFilter = {
      ...filter,
      startDate: toIsoDate(startDate),
      endDate: toIsoDate(endDate),
      skip: 0
    };
    setCurrentPage(0);
    setFilter(nextFilter);
    saveTransactionFilters(nextFilter);

    if (serverSearchActive) {
      runFilteredSearch(nextFilter);
    }
  };

  const getPreselectedProjectId = () => {
    if (copyingTransaction) return copyingTransaction.projectId;
    if (editingTransaction) return editingTransaction.projectId;
    return searchParams.get('projectId') ? parseInt(searchParams.get('projectId')!) : undefined;
  };

  const getPreselectedCustomerId = () => {
    if (copyingTransaction) {
      const customer = customers.find(c => c.projects.some(p => p.id === copyingTransaction.projectId));
      return customer?.id;
    }
    if (editingTransaction) {
      const customer = customers.find(c => c.projects.some(p => p.id === editingTransaction.projectId));
      return customer?.id;
    }
    return undefined;
  };

  // Filter options
  const directionSelectOptions = useMemo(() => directions.map(direction => ({
    value: direction.id,
    label: direction.title,
  })), [directions]);

  const directionFilterOptions = useMemo(() => [
    { value: -2, label: 'No value' },
    { value: -1, label: 'All' },
    ...directionSelectOptions,
  ], [directionSelectOptions]);

  const categorySelectOptions = useMemo(() => transactionCategories.map(category => ({
    value: category.id,
    label: category.title,
  })), [transactionCategories]);

  const categoryFilterOptions = useMemo(() => [
    { value: -2, label: 'All categories' },
    { value: -1, label: 'No category' },
    ...categorySelectOptions,
  ], [categorySelectOptions]);

  const transactionTypeOptions = useMemo(() => [
    { value: 0, label: 'All Types' },
    ...transactionTypes.map(type => ({
      value: type.id,
      label: type.title,
    })),
  ], [transactionTypes]);

  const categoryOptions = [
    { value: TransactionCategory.AllCategories, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.AllCategories] },
    { value: TransactionCategory.NoCategory, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.NoCategory] },
    { value: TransactionCategory.Development, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.Development] },
    { value: TransactionCategory.Design, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.Design] },
    { value: TransactionCategory.Marketing, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.Marketing] },
    { value: TransactionCategory.Support, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.Support] },
    { value: TransactionCategory.Management, label: TRANSACTION_CATEGORY_LABELS[TransactionCategory.Management] },
  ];

  const directionOptions = [
    { value: TransactionDirection.NoValue, label: TRANSACTION_DIRECTION_LABELS[TransactionDirection.NoValue] },
    { value: TransactionDirection.All, label: TRANSACTION_DIRECTION_LABELS[TransactionDirection.All] },
    { value: TransactionDirection.Alex, label: TRANSACTION_DIRECTION_LABELS[TransactionDirection.Alex] },
    { value: TransactionDirection.Mike, label: TRANSACTION_DIRECTION_LABELS[TransactionDirection.Mike] },
  ];

  const lookupLoading = directionsLoading || transactionTypesLoading || transactionCategoriesLoading;
  const existingTransactionFormIsLoading = Boolean(editingTransaction || copyingTransaction) && (
    referenceDataLoading ||
    lookupLoading
  );

  const renderTransactionFormLoader = () => (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="fade-in">
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div className="spinner-border" role="status"></div>
              <p style={{ color: 'var(--text-muted)' }}>Loading transaction...</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (showForm) {
    if (transactionFormDataLoading || existingTransactionFormIsLoading) {
      return renderTransactionFormLoader();
    }

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <TransactionForm
              transaction={editingTransaction || copyingTransaction}
              customers={customers}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              onDelete={handleFormDelete}
              onCopy={handleCopyTransaction}
              isLoading={formLoading}
              preselectedProjectId={getPreselectedProjectId()}
              preselectedCustomerId={getPreselectedCustomerId()}
              mode={editingTransaction ? 'edit' : copyingTransaction ? 'copy' : 'create'}
              directionOptions={directions}
              typeOptions={transactionTypes}
              categoryOptions={transactionCategories}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="page-header transactions-page-header">
        <div>
          <h1 style={{ 
            color: 'var(--text-primary)', 
            fontSize: 'var(--font-size-3xl)', 
            fontWeight: '700',
            marginBottom: '0.5rem' 
          }}>
            Transactions
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
            Manage your financial transactions
          </p>
        </div>
        <button
          className="btn btn-primary page-header-action"
          onClick={() => setShowForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          Create Transaction
        </button>
      </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Filters */}
          <div className="card filters-card mb-4">
            <div className="card-header">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h5 className="card-title">Filters</h5>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm ms-auto"
                  onClick={handleClearFilters}
                >
                  Clear filters
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-2">
                  <label htmlFor="transactionType" className="form-label">Transaction type</label>
                  <select
                    id="transactionType"
                    className="form-select"
                    value={filter.transactionTypeId}
                    onChange={(e) => handleFilterChange('transactionTypeId', parseInt(e.target.value))}
                  >
                    {transactionTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <label htmlFor="sourceId" className="form-label">Source</label>
                  <select
                    id="sourceId"
                    className="form-select"
                    value={filter.sourceId}
                    onChange={(e) => handleFilterChange('sourceId', parseInt(e.target.value))}
                  >
                    {directionFilterOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <label htmlFor="directionId" className="form-label">Direction</label>
                  <select
                    id="directionId"
                    className="form-select"
                    value={filter.directionId}
                    onChange={(e) => handleFilterChange('directionId', parseInt(e.target.value))}
                  >
                    {directionFilterOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label htmlFor="startDate" className="form-label">Start date</label>
                  <DateInput
                    id="startDate"
                    className="form-control"
                    value={filter.startDate}
                    maxDate={filter.endDate}
                    onChange={(value) => {
                      handleFilterChange('startDate', value);
                    }}
                  />
                </div>

                <div className="col-md-3">
                  <label htmlFor="endDate" className="form-label">End date</label>
                  <DateInput
                    id="endDate"
                    className="form-control"
                    value={filter.endDate}
                    minDate={filter.startDate}
                    onChange={(value) => {
                      handleFilterChange('endDate', value);
                    }}
                  />
                </div>
              </div>

              <div className="row g-3 mt-2">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2 date-preset-actions">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyDatePreset('today')}>Today</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyDatePreset('thisWeek')}>This week</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyDatePreset('thisMonth')}>This month</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyDatePreset('lastMonth')}>Last month</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyDatePreset('thisYear')}>This year</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyDatePreset('lastYear')}>Last year</button>
                  </div>
                </div>

                <div className="col-md-3">
                  <SearchableDropdown
                    id="category"
                    label="Category"
                    placeholder="All categories"
                    items={categoryFilterOptions}
                    value={filter.category}
                    onChange={(value) => handleFilterChange('category', parseInt(value as string, 10))}
                    getItemValue={(item) => item.value}
                    getItemLabel={(item) => item.label}
                    searchPlaceholder="Search categories..."
                    noItemsMessage="No categories found"
                    className="filter-dropdown"
                    size="md"
                  />
                </div>

                <div className="col-md-3">
                  <SearchableDropdown
                    id="customerId"
                    label="Customer"
                    placeholder="All Customers"
                    items={[{ id: 0, title: 'All Customers' }, ...customers]}
                    value={filter.customerId}
                    onChange={(value) => handleFilterChange('customerId', Number(value))}
                    getItemValue={(item) => item.id}
                    getItemLabel={(item) => item.title}
                    searchPlaceholder="Search customers..."
                    noItemsMessage="No customers found"
                    className="filter-dropdown"
                    size="md"
                  />
                </div>

                <div className="col-md-3">
                  <SearchableDropdown
                    id="projectId"
                    label="Project"
                    placeholder="All Projects"
                    items={[{ id: 0, title: 'All Projects' }, ...projects]}
                    value={filter.projectId}
                    onChange={(value) => handleFilterChange('projectId', Number(value))}
                    getItemValue={(item) => item.id}
                    getItemLabel={(item) => item.title}
                    searchPlaceholder="Search projects..."
                    noItemsMessage="No projects found"
                    className="filter-dropdown"
                    size="md"
                  />
                </div>

                <div className="col-md-3">
                  <label htmlFor="searchText" className="form-label">Description</label>
                  <div className="input-group">
                    <input
                      type="text"
                      id="searchText"
                      className="form-control"
                      placeholder="Search..."
                      value={filter.searchText}
                      onChange={(e) => handleFilterChange('searchText', e.target.value)}
                      onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={handleSearch}
                      title="Search"
                      aria-label="Search"
                    >
                      <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h5 className="card-title">Transactions</h5>
                <div className="ms-auto d-flex flex-wrap gap-2 transactions-view-switcher">
                  <button
                    type="button"
                    className={`btn btn-sm ${activeView === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveView('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activeView === 'incompleted' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveView('incompleted')}
                  >
                    Incomplete
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activeView === 'topDebts' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveView('topDebts')}
                  >
                    Top Customer Debts
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activeView === 'tbu' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveView('tbu')}
                  >
                    To be updated
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activeView === 'latestUpdated' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveView('latestUpdated')}
                  >
                    Latest updated
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '50vh',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  <div className="spinner-border" role="status"></div>
                  <p style={{ color: 'var(--text-muted)' }}>Loading transactions...</p>
                </div>
              ) : activeView === 'all' ? (
                <>
                  {filteredTransactions.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '1.5rem 0.75rem',
                      color: 'var(--text-muted)'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                      </div>
                      <h5>No transactions found</h5>
                      <p>Try adjusting your search filters or create a new transaction.</p>
                    </div>
                  ) : (
                    <>
                      <TransactionsTable
                        transactions={paginatedTransactions}
                        onEdit={handleEditTransaction}
                        onDelete={handleDeleteTransaction}
                        onCopy={handleCopyTransaction}
                        showActions={true}
                        showDirectionBadge={true}
                        directionBadgeColor="#4b5563"
                        sourceBadgeColor="#6b7280"
                        highlightPendingStatus={true}
                        pendingStatusColor="#111111"
                        statusBadgeClassName="badge contact-type-badge"
                      />
                      <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        itemsPerPage={pageSize}
                        onPageChange={goToPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={[10, 25, 50, 100]}
                      />
                    </>
                  )}
                </>
              ) : activeView === 'incompleted' ? (
                <>
                  <div className="alert alert-warning mb-3">
                    <strong>Total unpaid:</strong>{' '}
                    {(dashboardSnapshot?.totalUnpaidAmount ?? 0).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </div>
                  <div>
                    <h6 className="mb-2">Incomplete Transactions</h6>
                    {incompletedTransactions.length === 0 ? (
                      <p className="text-muted mb-0">No incomplete transactions found.</p>
                    ) : (
                      <>
                        <TransactionsTable
                          transactions={paginatedIncompletedTransactions}
                          showActions={true}
                          onEdit={handleEditTransaction}
                          onCopy={handleCopyTransaction}
                          editOnlyPending={true}
                          allowCopy={true}
                          highlightPendingRow={true}
                          pendingRowColor="#ffffff"
                          highlightPendingStatus={true}
                          pendingStatusColor="#111111"
                          statusBadgeClassName="badge contact-type-badge"
                          showDirectionBadge={true}
                          directionBadgeColor="#4b5563"
                          sourceBadgeColor="#6b7280"
                        />
                        <Pagination
                          currentPage={incompletedCurrentPage}
                          totalItems={incompletedTotalItems}
                          itemsPerPage={incompletedPageSize}
                          onPageChange={goToIncompletedPage}
                          onPageSizeChange={setIncompletedPageSize}
                          pageSizeOptions={[5, 10, 25, 50]}
                        />
                      </>
                    )}
                  </div>
                </>
              ) : activeView === 'topDebts' ? (
                <>
                  <div className="alert alert-warning mb-3">
                    <strong>Total unpaid:</strong>{' '}
                    {(dashboardSnapshot?.totalUnpaidAmount ?? 0).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </div>

                  <div>
                    <h6 className="mb-2">Top Customer Debts</h6>
                    {topCustomerDebts.length === 0 ? (
                      <p className="text-muted mb-0">No customer debts found.</p>
                    ) : (
                      <>
                        <CustomerDebtTable customerDebts={paginatedCustomerDebts} />
                        <Pagination
                          currentPage={debtsCurrentPage}
                          totalItems={debtsTotalItems}
                          itemsPerPage={debtsPageSize}
                          onPageChange={goToDebtsPage}
                          onPageSizeChange={setDebtsPageSize}
                          pageSizeOptions={[5, 10, 25, 50]}
                        />
                      </>
                    )}
                  </div>
                </>
              ) : activeView === 'tbu' ? (
                <>
                  {tbuTransactions.length === 0 ? (
                    <p className="text-muted mb-0">No transactions marked as TBU.</p>
                  ) : (
                    <>
                      <TransactionsTable
                        transactions={paginatedTbuTransactions}
                        showActions={true}
                        onEdit={handleEditTransaction}
                        onCopy={handleCopyTransaction}
                        showDirectionBadge={true}
                        directionBadgeColor="#4b5563"
                        sourceBadgeColor="#6b7280"
                      />
                      <Pagination
                        currentPage={tbuCurrentPage}
                        totalItems={tbuTotalItems}
                        itemsPerPage={tbuPageSize}
                        onPageChange={goToTbuPage}
                        onPageSizeChange={setTbuPageSize}
                        pageSizeOptions={[5, 10, 25, 50]}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  {latestUpdatedTransactions.length === 0 ? (
                    <p className="text-muted mb-0">No updated transactions found.</p>
                  ) : (
                    <>
                      <TransactionsTable
                        transactions={paginatedLatestUpdatedTransactions}
                        showActions={true}
                        onEdit={handleEditTransaction}
                        onCopy={handleCopyTransaction}
                        showDirectionBadge={true}
                        directionBadgeColor="#4b5563"
                        sourceBadgeColor="#6b7280"
                      />
                      <Pagination
                        currentPage={latestUpdatedCurrentPage}
                        totalItems={latestUpdatedTotalItems}
                        itemsPerPage={latestUpdatedPageSize}
                        onPageChange={goToLatestUpdatedPage}
                        onPageSizeChange={setLatestUpdatedPageSize}
                        pageSizeOptions={[5, 10, 25, 50]}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
    </div>
  );
};

export default Transactions;
